import { backendClient } from './backend'

export interface AdminCheckResult {
  ok: boolean
  isAdmin: boolean
  role?: string
  error?: string
}

export interface DeleteUserResult {
  ok: boolean
  error?: string
  mode?: 'archive' | 'delete'
}

export interface UpdateRoleResult {
  ok: boolean
  error?: string
  userId?: string
  newRole?: string
}

export interface ManageExtraRolesResult {
  ok: boolean
  error?: string
  userId?: string
  role?: string
  action?: 'grant' | 'revoke'
}

export interface VerifyUserResult {
  ok: boolean
  error?: string
  userId?: string
  isVerified?: boolean
}

export interface ManagePlayerVisibilityResult {
  ok: boolean
  error?: string
  updated?: number
  isPublic?: boolean
}

export const checkAdminAccess = async (): Promise<AdminCheckResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, isAdmin: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/check', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    let message = 'Unable to verify admin access'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures and use the generic message.
    }

    return { ok: false, isAdmin: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    isAdmin: Boolean(payload?.isAdmin),
    role: typeof payload?.role === 'string' ? payload.role : undefined,
  }
}

export const deleteUserAndData = async (
  userId: string,
  mode: 'archive' | 'delete' = 'archive'
): Promise<DeleteUserResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/delete-user', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, mode }),
  })

  if (!response.ok) {
    let message = 'Failed to delete user and data'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures and use the generic message.
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    mode: payload?.mode || mode,
  }
}

export const updateUserRole = async (userId: string, newRole: string): Promise<UpdateRoleResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/update-role', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, newRole }),
  })

  if (!response.ok) {
    let message = 'Failed to update role'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    userId: payload?.userId,
    newRole: payload?.newRole,
  }
}

export const manageExtraRole = async (
  userId: string,
  role: string,
  action: 'grant' | 'revoke'
): Promise<ManageExtraRolesResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/manage-extra-roles', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, role, action }),
  })

  if (!response.ok) {
    let message = 'Failed to manage extra role'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    userId: payload?.userId,
    role: payload?.role,
    action: payload?.action,
  }
}

export const verifyUser = async (userId: string, isVerified: boolean): Promise<VerifyUserResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/verify-user', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, isVerified }),
  })

  if (!response.ok) {
    let message = 'Failed to verify user'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    userId: payload?.userId,
    isVerified: payload?.isVerified,
  }
}

export const managePlayerVisibility = async (
  playerIds: string[],
  isPublic: boolean
): Promise<ManagePlayerVisibilityResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/manage-player-visibility', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ playerIds, isPublic }),
  })

  if (!response.ok) {
    let message = 'Failed to update player visibility'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    updated: payload?.updated,
    isPublic: payload?.isPublic,
  }
}

export interface ManageTopTalentResult {
  ok: boolean
  error?: string
  updated?: number
  isTopTalent?: boolean
}

export const manageTopTalent = async (
  playerIds: string[],
  isTopTalent: boolean
): Promise<ManageTopTalentResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) {
    return { ok: false, error: 'No active session' }
  }

  const response = await fetch('/api/admin/manage-top-talent', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ playerIds, isTopTalent }),
  })

  if (!response.ok) {
    let message = 'Failed to update top talent status'

    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error
      }
    } catch {
      // Ignore JSON parsing failures
    }

    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    updated: payload?.updated,
    isTopTalent: payload?.isTopTalent,
  }
}

export const toggleProStatus = async (
  userId: string,
  active: boolean,
  role?: string
): Promise<{ ok: boolean; error?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/toggle-pro', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, active, role }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to toggle Pro status' }
    } catch {
      return { ok: false, error: 'Failed to toggle Pro status' }
    }
  }

  return { ok: true }
}

export const overridePlayer = async (
  playerId: string,
  updates: Record<string, any>
): Promise<{ ok: boolean; error?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/override-player', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ playerId, updates }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to override player' }
    } catch {
      return { ok: false, error: 'Failed to override player' }
    }
  }

  return { ok: true }
}

// ── Admin User Control API ──

export interface UserDetailResult {
  ok: boolean
  error?: string
  profile?: any
  subscription?: any
  payments?: any[]
  referral?: { referredBy: any; downlines: any[] }
  player?: any
  club?: any
  stats?: { totalPayments: number; totalSpent: number; downlineCount: number; paidDownlines: number }
}

export const getUserDetail = async (userId: string): Promise<UserDetailResult> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch(`/api/admin/user-detail?id=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let message = 'Failed to fetch user detail'
    try {
      const payload = await response.json()
      if (typeof payload?.error === 'string' && payload.error.trim()) message = payload.error
    } catch { /* ignore */ }
    return { ok: false, error: message }
  }

  const payload = await response.json()
  return {
    ok: true,
    profile: payload?.profile,
    subscription: payload?.subscription,
    payments: payload?.payments || [],
    referral: payload?.referral || { referredBy: null, downlines: [] },
    player: payload?.player,
    club: payload?.club,
    stats: payload?.stats,
  }
}

export const suspendUser = async (userId: string): Promise<{ ok: boolean; error?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/suspend-user', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to suspend user' }
    } catch {
      return { ok: false, error: 'Failed to suspend user' }
    }
  }

  return { ok: true }
}

export const unsuspendUser = async (userId: string): Promise<{ ok: boolean; error?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/unsuspend-user', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to unsuspend user' }
    } catch {
      return { ok: false, error: 'Failed to unsuspend user' }
    }
  }

  return { ok: true }
}

export const extendPro = async (userId: string, days?: number): Promise<{ ok: boolean; error?: string; currentPeriodEnd?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/extend-pro', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, days }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to extend Pro' }
    } catch {
      return { ok: false, error: 'Failed to extend Pro' }
    }
  }

  const payload = await response.json()
  return { ok: true, currentPeriodEnd: payload?.currentPeriodEnd }
}

export const setProExpiry = async (userId: string, expiryDate: string): Promise<{ ok: boolean; error?: string; currentPeriodEnd?: string }> => {
  const {
    data: { session },
  } = await backendClient.auth.getSession()

  const accessToken = session?.access_token
  if (!accessToken) return { ok: false, error: 'No active session' }

  const response = await fetch('/api/admin/set-expiry', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, expiryDate }),
  })

  if (!response.ok) {
    try {
      const payload = await response.json()
      return { ok: false, error: payload?.error || 'Failed to set expiry' }
    } catch {
      return { ok: false, error: 'Failed to set expiry' }
    }
  }

  const payload = await response.json()
  return { ok: true, currentPeriodEnd: payload?.currentPeriodEnd }
}
